import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import {
  AlertTriangle, CheckCircle, Save, Loader2, ShieldAlert,
  ArrowUp, ArrowDown, Minus, Info, Sparkles
} from 'lucide-react';

const fmtPx = (n) => `$${(n || 0).toFixed(4)}`;
const fmtPct = (n) => `${n > 0 ? '+' : ''}${(n || 0).toFixed(2)}%`;

const FUND_ORDER = ['A Fund', 'B Fund', 'C Fund', 'D Fund', 'E Fund'];
const FUND_KEY_MAP = { 'A Fund': 'g', 'B Fund': 'f', 'C Fund': 'c', 'D Fund': 's', 'E Fund': 'i' };
const FUND_COLORS = { 'A Fund': '#6B7280', 'B Fund': '#2D5F8A', 'C Fund': '#1B2A4A', 'D Fund': '#8B6B3B', 'E Fund': '#3B8C6E' };
const FUND_DESCS = {
  'A Fund': 'U.S. Treasury Securities',
  'B Fund': 'Bloomberg U.S. Aggregate Bond',
  'C Fund': 'S&P 500',
  'D Fund': 'DJ U.S. Completion TSM',
  'E Fund': 'MSCI EAFE International',
};

export const FundAllocationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [funds, setFunds] = useState([]);
  const [ift, setIft] = useState({ used: 0, limit: 2, reset_date: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [presetBanner, setPresetBanner] = useState(null);

  // Editable allocations
  const [alloc, setAlloc] = useState({ g: 20, f: 20, c: 20, s: 20, i: 20 });
  const [initial, setInitial] = useState({ g: 20, f: 20, c: 20, s: 20, i: 20 });

  useEffect(() => { loadData(); }, []);

  // Apply preset from URL query (?preset=L_2045&a=18&b=6&c=38&d=12&e=26)
  useEffect(() => {
    const preset = searchParams.get('preset');
    if (!preset) return;
    const toNum = (v) => {
      const n = parseInt(v || '0', 10);
      return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
    };
    const a = toNum(searchParams.get('a'));
    const b = toNum(searchParams.get('b'));
    const c = toNum(searchParams.get('c'));
    const d = toNum(searchParams.get('d'));
    const e = toNum(searchParams.get('e'));
    const sum = a + b + c + d + e;
    if (sum === 100) {
      // FE keys: g→A, f→B, c→C, s→D, i→E
      setAlloc({ g: a, f: b, c: c, s: d, i: e });
      setPresetBanner({ code: preset, a, b, c, d, e });
    }
    // Clear params so reload doesn't re-apply
    const next = new URLSearchParams(searchParams);
    ['preset', 'a', 'b', 'c', 'd', 'e'].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fundsRes, iftRes] = await Promise.allSettled([
        api.get('/tsp/participant/fund-allocation'),
        api.get('/tsp/participant/ift-status'),
      ]);
      if (fundsRes.status === 'fulfilled') {
        const d = Array.isArray(fundsRes.value.data) ? fundsRes.value.data : [];
        setFunds(d);
        const a = {};
        d.forEach(f => { a[FUND_KEY_MAP[f.fund]] = f.current_pct; });
        setAlloc({ g: a.g ?? 20, f: a.f ?? 20, c: a.c ?? 20, s: a.s ?? 20, i: a.i ?? 20 });
        setInitial({ g: a.g ?? 20, f: a.f ?? 20, c: a.c ?? 20, s: a.s ?? 20, i: a.i ?? 20 });
      }
      if (iftRes.status === 'fulfilled') setIft(iftRes.value.data || {});
    } catch {} finally { setLoading(false); }
  };

  const total = useMemo(() => Object.values(alloc).reduce((s, v) => s + v, 0), [alloc]);
  const isDirty = useMemo(() => Object.keys(alloc).some(k => alloc[k] !== initial[k]), [alloc, initial]);
  const isValid = Math.abs(total - 100) < 0.01;
  const atLimit = (ift.used ?? 0) >= (ift.limit ?? 2);
  const lastIft = (ift.used ?? 0) === (ift.limit ?? 2) - 1;

  const resetStr = ift.reset_date
    ? new Date(ift.reset_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '--';

  const canSubmit = isDirty && isValid && !atLimit;

  const handleChange = (key, val) => {
    const num = Math.min(100, Math.max(0, parseFloat(val) || 0));
    setAlloc(prev => ({ ...prev, [key]: num }));
    setSaveMsg(''); setSaveErr('');
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (lastIft) {
      setShowConfirm(true);
    } else {
      doSave();
    }
  };

  const doSave = async () => {
    setShowConfirm(false);
    setSaving(true); setSaveErr(''); setSaveMsg('');
    try {
      const res = await api.put('/tsp/participant/fund-allocation', alloc);
      setSaveMsg(`Allocation saved. Effective: ${res.data.effective}. IFTs used: ${res.data.ift_used}/${ift.limit}`);
      setInitial({ ...alloc });
      setIft(prev => ({ ...prev, used: res.data.ift_used }));
    } catch (err) {
      setSaveErr(err.response?.data?.detail || 'Failed to save allocation');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="panel p-6 animate-pulse"><div className="h-6 bg-gray-100 rounded w-48 mb-4" /><div className="h-60 bg-gray-50 rounded" /></div>;

  return (
    <div className="space-y-4" data-testid="fund-allocation-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8B6B3B] mb-0.5" data-testid="fund-family-header">
            UniFed Core Funds&trade; — A through E Series
          </p>
          <h1 className="text-lg font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans' }}>Fund Allocation Editor</h1>
        </div>
        <button onClick={handleSubmit} disabled={!canSubmit || saving} data-testid="save-allocation-btn"
          className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded transition-colors ${canSubmit ? 'bg-[#1B2A4A] hover:bg-[#2D3F5F] text-white' : 'bg-[#E2E5E9] text-[#9CA3AF] cursor-not-allowed'}`}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Saving...' : 'Submit Allocation'}
        </button>
      </div>

      {/* Feedback */}
      {saveMsg && <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-4 py-3" data-testid="save-success"><CheckCircle size={14} className="text-emerald-600" /><p className="text-xs text-emerald-700">{saveMsg}</p></div>}
      {saveErr && <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded px-4 py-3" data-testid="save-error"><AlertTriangle size={14} className="text-red-600" /><p className="text-xs text-red-700">{saveErr}</p></div>}

      {/* Preset banner — triggered by ?preset=L_2045 deep-link from dashboard widget */}
      {presetBanner && (
        <div
          className="flex items-start gap-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded px-4 py-3"
          data-testid="allocation-preset-banner"
        >
          <Sparkles size={16} className="text-[#2D5F8A] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-[#1B2A4A]">
              Allocation pre-filled from recommendation: <span className="font-mono">{presetBanner.code}</span>
            </p>
            <p className="text-[11px] text-[#6B7280] mt-0.5">
              Review the recommended allocation below and click <span className="font-semibold">Submit Allocation</span> to apply it
              (counts as 1 of your {ift.limit} monthly IFTs).
            </p>
          </div>
          <button
            onClick={() => {
              setAlloc({ ...initial });
              setPresetBanner(null);
            }}
            className="text-[11px] text-[#2D5F8A] hover:underline"
            data-testid="allocation-preset-reset"
          >
            Revert
          </button>
        </div>
      )}

      {/* Validation */}
      {!isValid && isDirty && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded px-4 py-3" data-testid="validation-error">
          <AlertTriangle size={14} className="text-red-600" />
          <p className="text-xs text-red-700">Allocations must total exactly 100%. Current total: <span className="font-mono font-semibold">{total.toFixed(1)}%</span></p>
        </div>
      )}

      {/* IFT Status */}
      <div className={`panel p-4 ${atLimit ? 'bg-red-50 border-red-200' : 'bg-white'}`} data-testid="ift-status-bar">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {atLimit ? <ShieldAlert size={14} className="text-red-600" /> : <Info size={14} className="text-[#6B7280]" />}
            <span className="text-xs font-medium text-[#1B2A4A]" data-testid="ift-usage-text">
              {ift.used} of {ift.limit} monthly transfers used. Resets {resetStr}.
            </span>
          </div>
          <span className="text-[10px] font-mono text-[#9CA3AF]">Recordkeeping Bulletin 08-6</span>
        </div>
        {atLimit && (
          <p className="mt-2 text-xs text-red-700 font-medium" data-testid="ift-limit-message">
            Monthly IFT limit reached. Only A Fund transfers are permitted until {resetStr}. (Recordkeeping Bulletin 08-6)
          </p>
        )}
      </div>

      {/* Last IFT warning confirmation dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="confirm-overlay">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" data-testid="confirm-dialog">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-[#1B2A4A] mb-1">Final IFT This Month</h3>
                <p className="text-xs text-[#6B7280] leading-relaxed">
                  This will use your final permitted transfer this month. After submission, only A Fund transfers are permitted until <span className="font-semibold">{resetStr}</span>.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowConfirm(false)} className="px-4 py-2 text-sm border border-[#D1D5DB] rounded text-[#6B7280] hover:bg-gray-50" data-testid="confirm-cancel">Cancel</button>
              <button onClick={doSave} className="px-4 py-2 text-sm bg-[#1B2A4A] text-white rounded hover:bg-[#2D3F5F]" data-testid="confirm-submit">Confirm & Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* Fund Table */}
      <div className="panel overflow-hidden" data-testid="fund-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F8F9FA] border-b border-[#E2E5E9] text-xs text-[#6B7280] uppercase tracking-wider">
              <th className="text-left py-3 px-4">Fund</th>
              <th className="text-right py-3 px-4">Price</th>
              <th className="text-right py-3 px-4">Day Change</th>
              <th className="text-right py-3 px-4">YTD Return</th>
              <th className="text-center py-3 px-4 w-44">Allocation %</th>
            </tr>
          </thead>
          <tbody>
            {FUND_ORDER.map((fundName) => {
              const fund = funds.find(f => f.fund === fundName) || {};
              const key = FUND_KEY_MAP[fundName];
              const pct = alloc[key];
              const isUp = fund.day_change > 0;
              const isDown = fund.day_change < 0;
              const chgColor = isUp ? 'text-emerald-600' : isDown ? 'text-red-600' : 'text-[#6B7280]';
              const ChgIcon = isUp ? ArrowUp : isDown ? ArrowDown : Minus;
              const ytdColor = fund.ytd_return > 0 ? 'text-emerald-600' : fund.ytd_return < 0 ? 'text-red-600' : 'text-[#6B7280]';
              const disabled = atLimit && key !== 'g';

              return (
                <tr key={fundName} className="border-b border-[#F0F1F3] hover:bg-[#FAFBFC]" data-testid={`fund-row-${key}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: FUND_COLORS[fundName] }} />
                      <div>
                        <p className="font-medium text-[#1B2A4A]">{fundName}</p>
                        <p className="text-[10px] text-[#9CA3AF]">{FUND_DESCS[fundName]}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-[#1B2A4A]" data-testid={`price-${key}`}>{fmtPx(fund.price)}</td>
                  <td className="py-3 px-4 text-right">
                    <span className={`inline-flex items-center gap-0.5 font-mono text-xs ${chgColor}`}>
                      <ChgIcon size={10} />{fmtPct(fund.day_change_pct)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className={`font-mono text-xs font-medium ${ytdColor}`} data-testid={`ytd-${key}`}>{fmtPct(fund.ytd_return)}</span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <input
                        type="number" min={0} max={100} step={1}
                        value={pct} onChange={e => handleChange(key, e.target.value)}
                        disabled={disabled}
                        data-testid={`alloc-input-${key}`}
                        className={`w-20 h-9 text-center font-mono text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#1B2A4A]/20 ${
                          disabled ? 'bg-gray-100 text-[#9CA3AF] border-[#E2E5E9] cursor-not-allowed' : 'bg-white text-[#1B2A4A] border-[#D1D5DB] focus:border-[#1B2A4A]'
                        }`}
                      />
                      <span className="text-xs text-[#6B7280]">%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-[#F8F9FA]">
              <td colSpan={4} className="py-3 px-4 text-right text-xs font-semibold text-[#1B2A4A]">Total</td>
              <td className="py-3 px-4">
                <div className="flex items-center justify-center gap-2">
                  <span className={`font-mono text-sm font-bold ${isValid ? 'text-emerald-600' : 'text-red-600'}`} data-testid="allocation-total">
                    {total.toFixed(1)}%
                  </span>
                  {isValid ? <CheckCircle size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-red-500" />}
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Allocation bar */}
      <div className="panel p-4">
        <span className="overline mb-2 block">Allocation Preview</span>
        <div className="flex h-4 rounded-sm overflow-hidden">
          {FUND_ORDER.map(fn => {
            const key = FUND_KEY_MAP[fn];
            return <div key={fn} className="transition-all" style={{ width: `${alloc[key]}%`, backgroundColor: FUND_COLORS[fn] }} title={`${fn}: ${alloc[key]}%`} />;
          })}
        </div>
        <div className="flex justify-between mt-2">
          {FUND_ORDER.map(fn => {
            const key = FUND_KEY_MAP[fn];
            return (
              <div key={fn} className="flex items-center gap-1 text-[9px] text-[#6B7280]">
                <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: FUND_COLORS[fn] }} />
                <span className="font-mono">{fn.split(' ')[0]} {alloc[key]}%</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-[10px] text-[#9CA3AF] text-center">
        Interfund Transfer rules: Recordkeeping Bulletin 08-6 · 5 C.F.R. § 1653 · Changes effective next business day
      </div>
    </div>
  );
};
